import { useState, useRef, useEffect } from 'react';

const THRESHOLD_MIN = 20;
const THRESHOLD_TRIGGER = 60;
const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isCoarsePointer = window.matchMedia("(pointer : coarse)").matches;
  return isMobileUA || isTouchDevice && isCoarsePointer;
};
const isGalaxyA23Device = () => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /SM-A23/i.test(ua);
};
const isAndroidDevice = () => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android/i.test(ua);
};
const isCustomKeyboardInput = (el) => {
  if (!el) return false;
  return el.closest('[data-custom-keyboard="true"]') !== null || el.getElementsByClassName("not-focus") !== null;
};
const isStandardOsInput = (el) => {
  if (!el) return false;
  if (isCustomKeyboardInput(el)) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) {
    if (el.readOnly || el.disabled) return false;
    const excludedTypes = [
      "checkbox",
      "radio",
      "button",
      "submit",
      "image",
      "reset",
      "file",
      "range",
      "color"
    ];
    return !excludedTypes.includes(el.type);
  }
  return false;
};
const isAnyFocusableInput = (el) => {
  if (!el) return false;
  return isStandardOsInput(el) || isCustomKeyboardInput(el);
};
const getScrollParent = (node) => {
  if (!node || node === document.body) return null;
  const overflowY = window.getComputedStyle(node).overflowY;
  const isScrollable = overflowY === "auto" || overflowY === "scroll";
  if (isScrollable && node.scrollHeight > node.clientHeight) {
    return node;
  }
  return getScrollParent(node.parentElement);
};
const scrollElementToCenter = (targetEl, customPadHeight = 0) => {
  if (!targetEl) return;
  if (isAndroidDevice()) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        targetEl.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      });
    });
  } else {
    const viewport = window.visualViewport;
    const effectiveViewportHeight = (viewport ? viewport.height : window.innerHeight) - customPadHeight;
    const rect = targetEl.getBoundingClientRect();
    const absoulteElemetTop = rect.top + window.pageYOffset;
    const elementCenterOffset = absoulteElemetTop + rect.height / 2;
    const targetScrollY = Math.max(0, elementCenterOffset - effectiveViewportHeight / 2);
    const scrollableParent = getScrollParent(targetEl);
    if (scrollableParent && scrollableParent !== document.body && scrollableParent !== document.documentElement) {
      const parentRect = scrollableParent.getBoundingClientRect();
      const relativeTop = rect.top - parentRect.top + scrollableParent.scrollTop;
      const targetParentScroll = relativeTop - (scrollableParent.clientHeight - customPadHeight) / 2 + rect.height / 2;
      scrollableParent.scrollTo({ top: Math.max(0, targetParentScroll), behavior: "smooth" });
    } else {
      window.scrollTo({ top: targetScrollY, behavior: "smooth" });
    }
  }
};
const useKeyboardScroll = ({ enabled = true } = {}) => {
  const [keyboardInset, setKeyboardInset] = useState(0);
  const insetRef = useRef(0);
  const rafRef = useRef(null);
  const timersRef = useRef([]);
  const focusOutTimerRef = useRef(null);
  const initialScreenHeightRef = useRef(0);
  const isFocusingRef = useRef(false);
  const isFirstFocusRef = useRef(true);
  const isTransitioningRef = useRef(false);
  const safeSetTimeout = (fn, delay) => {
    const timerId = window.setTimeout(() => {
      fn();
      timersRef.current = timersRef.current.filter((id) => id !== timerId);
    }, delay);
    timersRef.current.push(timerId);
    return timerId;
  };
  const clearAllTimers = () => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  };
  useEffect(() => {
    const isMobile = isMobileDevice();
    if (!enabled || !isMobile) {
      insetRef.current = 0;
      setKeyboardInset(0);
      document.documentElement.style.setProperty("--keyboard-inset", "0px");
      return;
    }
    const viewport = window.visualViewport;
    if (!viewport) return;
    const isA23 = isGalaxyA23Device();
    const scrollDelay = isA23 ? 500 : 200;
    initialScreenHeightRef.current = Math.max(window.innerHeight, window.screen.height || 0);
    const update = () => {
      if (isTransitioningRef.current) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const activeEl = document.activeElement;
        const isOsInput = isStandardOsInput(activeEl);
        isFocusingRef.current = isOsInput;
        const baseHeight = Math.max(window.innerHeight, initialScreenHeightRef.current);
        const rawInset = Math.max(0, baseHeight - viewport.height);
        const prev = insetRef.current;
        let next;
        if (!isOsInput) {
          next = 0;
        } else if (rawInset > THRESHOLD_TRIGGER) {
          next = Math.floor(rawInset);
        } else if (rawInset < THRESHOLD_MIN && !isFocusingRef.current) {
          next = 0;
        } else {
          next = prev;
        }
        if (next !== prev) {
          insetRef.current = next;
          setKeyboardInset(next);
          document.documentElement.style.setProperty("--keyboard-inset", `${next}px}`);
        }
      });
    };
    const handleFocusIn = (e) => {
      if (focusOutTimerRef.current) {
        clearTimeout(focusOutTimerRef.current);
        focusOutTimerRef.current = null;
      }
      const el = e.target;
      if (isAnyFocusableInput(el)) {
        const isOsInput = isStandardOsInput(el);
        const isCustomKeypad = el.getAttribute("data-custom-keyboard") === "true";
        isTransitioningRef.current = true;
        safeSetTimeout(() => {
          isTransitioningRef.current = false;
        }, 300);
        if (isOsInput) {
          isFocusingRef.current = true;
          update();
          const delay = isFirstFocusRef.current ? scrollDelay + 100 : scrollDelay;
          isFirstFocusRef.current = false;
          safeSetTimeout(() => {
            if (document.activeElement === el) {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  scrollElementToCenter(el);
                });
              });
            }
          }, delay);
          safeSetTimeout(() => {
            if (document.activeElement === el) {
              scrollElementToCenter(el);
            }
          }, delay + 150);
        } else if (isCustomKeypad) {
          insetRef.current = 0;
          setKeyboardInset(0);
          document.documentElement.style.setProperty("--keyboard-inset", "0px");
          safeSetTimeout(() => {
            if (document.activeElement === el) {
              const container = el.closest('[data-custom-keyboard="true]');
              const customPadHeight = Number(
                container?.getAttribute("data-keypad-height")
              );
              scrollElementToCenter(el, customPadHeight);
            }
          }, 200);
        }
      }
    };
    const handleBlurFocusOut = () => {
      if (focusOutTimerRef.current) {
        clearTimeout(focusOutTimerRef.current);
      }
      focusOutTimerRef.current = safeSetTimeout(() => {
        const active = document.activeElement;
        if (!isAnyFocusableInput(active) && !isTransitioningRef.current) {
          isFocusingRef.current = false;
          update();
        }
      }, scrollDelay);
    };
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    document.addEventListener("focusin", handleFocusIn, true);
    document.addEventListener("focusout", handleBlurFocusOut, true);
    document.addEventListener("blur", handleBlurFocusOut, true);
    update();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearAllTimers();
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      document.removeEventListener("focusin", handleFocusIn, true);
      document.removeEventListener("focusout", handleBlurFocusOut, true);
      document.removeEventListener("blur", handleBlurFocusOut, true);
      insetRef.current = 0;
      isFocusingRef.current = false;
      document.documentElement.style.setProperty("--keyboard-inset", "0px");
    };
  }, [enabled]);
  return { keyboardInset, isKeyboardOpen: keyboardInset > 0 };
};

export { useKeyboardScroll };
